'use client';

import { useState } from 'react';
import { RefreshCw, User, MessageSquare, FileText, Lightbulb, CheckCircle } from 'lucide-react';
import ToolHero from '@/components/tools/ToolHero';
import ToolCTA from '@/components/tools/ToolCTA';
import CopyButton from '@/components/tools/CopyButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type Perspective = 'First' | 'Third';
type Tone = 'Professional' | 'Friendly' | 'Bold';

interface FormData {
  fullName: string;
  currentTitle: string;
  company: string;
  industry: string;
  yearsExperience: string;
  skills: string;
  achievement: string;
  personalTouch: string;
  perspective: Perspective;
  tone: Tone;
}

interface GeneratedBio {
  label: string;
  description: string;
  text: string;
  targetWords: string;
  icon: React.ReactNode;
}

/* ------------------------------------------------------------------ */
/*  Utility helpers                                                    */
/* ------------------------------------------------------------------ */

function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

/** Return the subject pronoun based on perspective and name */
function subjectPronoun(p: Perspective, name: string): string {
  return p === 'First' ? 'I' : name;
}

function possessivePronoun(p: Perspective, name: string): string {
  return p === 'First' ? 'my' : `${name}'s`;
}

function objectForm(p: Perspective): string {
  return p === 'First' ? 'me' : 'them';
}

function verbForm(p: Perspective, firstV: string, thirdV: string): string {
  return p === 'First' ? firstV : thirdV;
}

function parseSkills(raw: string): [string, string, string] {
  const parts = raw
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return [parts[0] || 'strategic thinking', parts[1] || 'leadership', parts[2] || 'innovation'];
}

/* ------------------------------------------------------------------ */
/*  Word banks for variation                                           */
/* ------------------------------------------------------------------ */

const passionVerbs = [
  'passionate about', 'dedicated to', 'driven by a commitment to',
  'focused on', 'devoted to', 'motivated by',
];

const impactPhrases = [
  'delivering measurable results', 'driving meaningful impact',
  'creating lasting value', 'making a tangible difference',
  'producing real-world outcomes', 'generating transformative results',
];

const leadershipPhrases = [
  'a proven track record of leadership', 'extensive leadership experience',
  'a strong foundation in team leadership', 'a history of guiding high-performing teams',
  'deep experience leading cross-functional initiatives',
];

const connectorPhrases = [
  'With a unique blend of', 'Combining expertise in', 'Bringing together',
  'Drawing on strengths in', 'Leveraging deep knowledge of',
];

const closingOneLinerPhrases = [
  'turning vision into results', 'where strategy meets execution',
  'bridging ideas and impact', 'building what matters',
  'making complexity simple', 'creating clarity from chaos',
];

const outsideWorkPhrases = [
  'Outside of work', 'Beyond the office', 'When not working',
  'Away from the desk', 'In addition to professional pursuits',
];

/* ------------------------------------------------------------------ */
/*  One-liner templates (~25 words) - 6 variations                    */
/* ------------------------------------------------------------------ */

function generateOneLiner(d: FormData): string {
  const [s1, s2] = parseSkills(d.skills);
  const subj = subjectPronoun(d.perspective, d.fullName);
  const v = verbForm(d.perspective, 'am', 'is');
  const years = d.yearsExperience ? `${d.yearsExperience}+ years` : 'extensive experience';
  const industry = d.industry || 'the industry';

  const templates: Record<Tone, string[]> = {
    Professional: [
      `${subj} ${v} a ${d.currentTitle}${d.company ? ` at ${d.company}` : ''} with ${years} in ${industry}, specializing in ${s1} and ${s2}.`,
      `${d.perspective === 'First' ? '' : `${d.fullName} is `}${d.perspective === 'First' ? 'A ' : 'a '}${d.currentTitle} with ${years} of ${industry} expertise, ${pickRandom(passionVerbs)} ${pickRandom(impactPhrases)}.`,
      `${d.perspective === 'First' ? 'As a' : `${d.fullName} is a`} ${d.currentTitle} in ${industry}, ${subj} ${verbForm(d.perspective, 'bring', 'brings')} ${years} of experience in ${s1} and ${s2} -- ${pickRandom(closingOneLinerPhrases)}.`,
      `${subj} ${v} a results-driven ${d.currentTitle} helping ${industry} organizations excel through ${s1} and ${s2}.`,
      `With ${years} in ${industry}, ${subj} ${v} a ${d.currentTitle}${d.company ? ` at ${d.company}` : ''} focused on ${s1} and ${pickRandom(impactPhrases)}.`,
      `${d.perspective === 'First' ? 'Experienced' : `${d.fullName} is an experienced`} ${d.currentTitle} specializing in ${s1} and ${s2}, with a proven ability to ${pickRandom(closingOneLinerPhrases)}.`,
    ],
    Friendly: [
      `${subj} ${v} a ${d.currentTitle}${d.company ? ` at ${d.company}` : ''} who loves ${s1} and helping ${industry} teams thrive -- with ${years} of experience and counting.`,
      `${d.perspective === 'First' ? 'Hi! I\'m' : `Meet ${d.fullName} --`} a ${d.currentTitle} with ${years} in ${industry}, ${pickRandom(passionVerbs)} ${s1} and ${pickRandom(closingOneLinerPhrases)}.`,
      `${years} in ${industry} taught ${objectForm(d.perspective)} that great ${s1} and ${s2} can change everything -- and ${subj} ${verbForm(d.perspective, 'love', 'loves')} proving it every day as a ${d.currentTitle}.`,
      `${subj} ${v} a ${d.currentTitle} who ${verbForm(d.perspective, 'believe', 'believes')} in the power of ${s1} and ${s2} to create real change in ${industry}.`,
      `${d.perspective === 'First' ? 'I\'m' : `${d.fullName} is`} a curious, driven ${d.currentTitle} with ${years} in ${industry} and a love for ${s1} and ${pickRandom(closingOneLinerPhrases)}.`,
      `Part ${s1} expert, part ${s2} enthusiast -- ${subj} ${v} a ${d.currentTitle} with ${years} of ${industry} experience and a knack for ${pickRandom(closingOneLinerPhrases)}.`,
    ],
    Bold: [
      `${subj} ${v} a ${d.currentTitle} who ${verbForm(d.perspective, 'don\'t', 'doesn\'t')} just work in ${industry} -- ${subj} ${verbForm(d.perspective, 'transform', 'transforms')} it, with ${years} of results to prove it.`,
      `${years} in ${industry}. ${d.currentTitle}. ${s1} and ${s2} expert. ${d.perspective === 'First' ? 'I' : d.fullName} ${verbForm(d.perspective, 'build', 'builds')} things that matter.`,
      `${d.perspective === 'First' ? 'I\'m' : `${d.fullName} is`} a ${d.currentTitle} on a mission to redefine what ${s1} and ${s2} can do in ${industry}.`,
      `${years} of ${industry} experience, one ${d.currentTitle} who ${verbForm(d.perspective, 'refuse', 'refuses')} to settle for average -- ${pickRandom(closingOneLinerPhrases)}.`,
      `The ${industry} world needs more ${s1} and ${s2}. ${subj} ${v} a ${d.currentTitle} who ${verbForm(d.perspective, 'deliver', 'delivers')} both.`,
      `${d.perspective === 'First' ? 'Meet me' : `Meet ${d.fullName}`}: ${d.currentTitle}, ${industry} disruptor, and relentless champion of ${s1} and ${pickRandom(impactPhrases)}.`,
    ],
  };

  return pickRandom(templates[d.tone]);
}

/* ------------------------------------------------------------------ */
/*  Short paragraph templates (~75 words) - 6 variations              */
/* ------------------------------------------------------------------ */

function generateShortParagraph(d: FormData): string {
  const [s1, s2, s3] = parseSkills(d.skills);
  const subj = subjectPronoun(d.perspective, d.fullName);
  const v = verbForm(d.perspective, 'am', 'is');
  const poss = possessivePronoun(d.perspective, d.fullName);
  const years = d.yearsExperience ? `${d.yearsExperience}+ years` : 'extensive experience';
  const industry = d.industry || 'the industry';
  const achievement = d.achievement || `${pickRandom(impactPhrases)} across multiple organizations`;
  const company = d.company ? ` at ${d.company}` : '';

  const templates: Record<Tone, string[]> = {
    Professional: [
      `${subj} ${v} a ${d.currentTitle}${company} with ${years} of experience in ${industry}. Specializing in ${s1}, ${s2}, and ${s3}, ${subj} ${verbForm(d.perspective, 'have', 'has')} a proven track record of ${achievement}. ${connectorPhrases[0]} technical expertise and strategic vision, ${subj} ${verbForm(d.perspective, 'help', 'helps')} organizations navigate complexity and achieve ${pickRandom(impactPhrases)}. ${subj} ${v} ${pickRandom(passionVerbs)} empowering teams and ${pickRandom(closingOneLinerPhrases)}.`,
      `With ${years} in ${industry}, ${subj} ${verbForm(d.perspective, 'bring', 'brings')} deep expertise in ${s1}, ${s2}, and ${s3} to ${poss} role as ${d.currentTitle}${company}. ${poss.charAt(0).toUpperCase() + poss.slice(1)} career highlights include ${achievement}. ${subj} ${v} known for ${pickRandom(leadershipPhrases)} and a commitment to ${pickRandom(impactPhrases)}.`,
      `${subj} ${v} an experienced ${d.currentTitle}${company} who ${verbForm(d.perspective, 'specialize', 'specializes')} in ${s1} and ${s2} within the ${industry} sector. Over ${years}, ${subj} ${verbForm(d.perspective, 'have', 'has')} consistently delivered results, including ${achievement}. ${subj} ${v} ${pickRandom(passionVerbs)} leveraging ${s3} to drive organizational success and ${pickRandom(impactPhrases)}.`,
      `As a ${d.currentTitle}${company}, ${subj} ${verbForm(d.perspective, 'combine', 'combines')} ${years} of ${industry} expertise with a strong foundation in ${s1}, ${s2}, and ${s3}. ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}, and ${v} committed to ${pickRandom(impactPhrases)} for every team and organization ${subj} ${verbForm(d.perspective, 'work', 'works')} with.`,
      `${pickRandom(connectorPhrases)} ${s1}, ${s2}, and ${s3}, ${subj} ${verbForm(d.perspective, 'serve', 'serves')} as a ${d.currentTitle}${company} in the ${industry} space. With ${years} of hands-on experience, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. ${subj} ${v} dedicated to ${pickRandom(impactPhrases)} and building high-performing teams.`,
      `${subj} ${v} a ${d.currentTitle}${company} with ${years} of ${industry} experience. ${poss.charAt(0).toUpperCase() + poss.slice(1)} areas of expertise include ${s1}, ${s2}, and ${s3}. Notable accomplishments include ${achievement}. ${subj} ${v} driven by a desire to ${pickRandom(closingOneLinerPhrases)} and deliver ${pickRandom(impactPhrases)}.`,
    ],
    Friendly: [
      `${d.perspective === 'First' ? 'Hi! I\'m' : `${d.fullName} is`} a ${d.currentTitle}${company} with ${years} in ${industry}. ${subj} ${verbForm(d.perspective, 'love', 'loves')} working at the intersection of ${s1}, ${s2}, and ${s3} -- and ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement} along the way. ${pickRandom(outsideWorkPhrases)}, ${d.personalTouch ? `you can find ${objectForm(d.perspective)} ${d.personalTouch}` : `${subj} ${v} always learning something new`}. ${subj} ${v} ${pickRandom(passionVerbs)} ${pickRandom(closingOneLinerPhrases)}.`,
      `${subj} ${v} a ${d.currentTitle}${company} who ${verbForm(d.perspective, 'thrive', 'thrives')} on ${s1} and ${s2} in the ${industry} world. With ${years} under ${poss} belt, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. What gets ${objectForm(d.perspective)} up in the morning? The chance to ${pickRandom(closingOneLinerPhrases)} and collaborate with great people.${d.personalTouch ? ` When not at work, ${subj} ${v} usually ${d.personalTouch}.` : ''}`,
      `With ${years} in ${industry}, ${subj} ${verbForm(d.perspective, 'have', 'has')} found ${poss} sweet spot as a ${d.currentTitle}${company}, blending ${s1} and ${s2} with a genuine love for ${s3}. ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement} -- and ${subj} ${v} just getting started.${d.personalTouch ? ` ${pickRandom(outsideWorkPhrases)}, you'll find ${objectForm(d.perspective)} ${d.personalTouch}.` : ''}`,
      `People say ${subj} ${v} a ${d.currentTitle} who makes ${s1} feel approachable. With ${years} in ${industry}${company}, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. ${subj} ${verbForm(d.perspective, 'believe', 'believes')} great work happens when ${s2} and ${s3} come together.${d.personalTouch ? ` Fun fact: ${subj} also ${verbForm(d.perspective, 'enjoy', 'enjoys')} ${d.personalTouch}.` : ''}`,
      `${d.perspective === 'First' ? 'I\'m' : `${d.fullName} is`} a ${d.currentTitle}${company} who ${verbForm(d.perspective, 'believe', 'believes')} ${industry} should be exciting, not intimidating. Over ${years}, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement} by focusing on ${s1}, ${s2}, and ${s3}. Always curious, always growing.${d.personalTouch ? ` Also: ${d.personalTouch}.` : ''}`,
      `${years} in ${industry} have taught ${objectForm(d.perspective)} that the best results come from combining ${s1}, ${s2}, and a genuine passion for ${s3}. As a ${d.currentTitle}${company}, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}.${d.personalTouch ? ` ${pickRandom(outsideWorkPhrases)}, ${subj} ${v} usually found ${d.personalTouch}.` : ''} Let's connect!`,
    ],
    Bold: [
      `${subj} ${v} a ${d.currentTitle}${company} who ${verbForm(d.perspective, 'refuse', 'refuses')} to play it safe. With ${years} in ${industry}, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement} -- and that's just the beginning. ${subj} ${verbForm(d.perspective, 'specialize', 'specializes')} in ${s1}, ${s2}, and ${s3}, and ${subj} ${v} here to ${pickRandom(closingOneLinerPhrases)}. The status quo doesn't stand a chance.`,
      `${years}. ${d.currentTitle}. ${industry}. ${subj} ${verbForm(d.perspective, 'have', 'has')} seen it all, and ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement} to show for it. ${subj} ${verbForm(d.perspective, 'lead', 'leads')} with ${s1}, execute with ${s2}, and never stop pushing the limits of ${s3}. ${d.perspective === 'First' ? 'Ready to' : `${d.fullName} is ready to`} ${pickRandom(closingOneLinerPhrases)}.`,
      `Most ${d.currentTitle}s follow the playbook. ${subj} ${verbForm(d.perspective, 'rewrite', 'rewrites')} it. With ${years} of ${industry} experience, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. ${poss.charAt(0).toUpperCase() + poss.slice(1)} weapons of choice: ${s1}, ${s2}, and ${s3}. ${pickRandom(impactPhrases)} isn't a goal -- it's the baseline.`,
      `${subj} ${verbForm(d.perspective, 'don\'t', 'doesn\'t')} just occupy the ${d.currentTitle} role${company} -- ${subj} ${verbForm(d.perspective, 'own', 'owns')} it. ${years} in ${industry} have sharpened ${poss} expertise in ${s1}, ${s2}, and ${s3}. Results? ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. There's more where that came from.`,
      `In ${industry}, the difference between good and great is execution. ${subj} ${v} a ${d.currentTitle}${company} with ${years} of experience who ${verbForm(d.perspective, 'make', 'makes')} great happen through ${s1}, ${s2}, and ${s3}. Case in point: ${achievement}.`,
      `${subj} ${verbForm(d.perspective, 'didn\'t', 'didn\'t')} spend ${years} in ${industry} to play small. As a ${d.currentTitle}${company}, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement} by going all-in on ${s1} and ${s2}. ${subj} ${v} ${pickRandom(passionVerbs)} ${pickRandom(closingOneLinerPhrases)} -- and ${subj} ${v} just getting warmed up.`,
    ],
  };

  return pickRandom(templates[d.tone]);
}

/* ------------------------------------------------------------------ */
/*  Full bio templates (~150-200 words) - 6 variations                */
/* ------------------------------------------------------------------ */

function generateFullBio(d: FormData): string {
  const [s1, s2, s3] = parseSkills(d.skills);
  const subj = subjectPronoun(d.perspective, d.fullName);
  const v = verbForm(d.perspective, 'am', 'is');
  const poss = possessivePronoun(d.perspective, d.fullName);
  const years = d.yearsExperience ? `${d.yearsExperience}+ years` : 'extensive experience';
  const industry = d.industry || 'the industry';
  const achievement = d.achievement || `${pickRandom(impactPhrases)} across multiple organizations`;
  const company = d.company ? ` at ${d.company}` : '';
  const personalTouch = d.personalTouch;
  const personalSentence = personalTouch
    ? `${pickRandom(outsideWorkPhrases)}, ${subj} ${verbForm(d.perspective, 'enjoy', 'enjoys')} ${personalTouch}. This creative outlet fuels ${poss} professional work and keeps ${objectForm(d.perspective)} inspired.`
    : `${subj} ${v} a lifelong learner who ${verbForm(d.perspective, 'believe', 'believes')} that continuous growth is the key to sustained excellence.`;

  const templates: Record<Tone, string[]> = {
    Professional: [
      `${subj} ${v} a ${d.currentTitle}${company} with ${years} of experience in the ${industry} sector. ${poss.charAt(0).toUpperCase() + poss.slice(1)} professional expertise spans ${s1}, ${s2}, and ${s3}, and ${subj} ${verbForm(d.perspective, 'have', 'has')} built a career around ${pickRandom(impactPhrases)} for organizations of all sizes.

Throughout ${poss} career, ${subj} ${verbForm(d.perspective, 'have', 'has')} consistently demonstrated ${pickRandom(leadershipPhrases)} and a talent for translating complex challenges into actionable strategies. Most notably, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. This accomplishment reflects ${poss} commitment to excellence and ability to drive meaningful outcomes.

${subj} ${v} ${pickRandom(passionVerbs)} staying at the forefront of ${industry} trends, and ${subj} ${verbForm(d.perspective, 'believe', 'believes')} that the intersection of ${s1} and ${s2} presents some of the most compelling opportunities for organizations today. ${subj} ${verbForm(d.perspective, 'approach', 'approaches')} every challenge with a blend of analytical rigor and creative problem-solving.

${personalSentence} ${subj} ${v} always open to connecting with fellow professionals and exploring opportunities to collaborate.`,

      `With ${years} of experience in ${industry}, ${subj} ${verbForm(d.perspective, 'have', 'has')} established ${d.perspective === 'First' ? 'myself' : `${d.fullName}`} as a ${d.currentTitle}${company} known for ${pickRandom(impactPhrases)}. ${poss.charAt(0).toUpperCase() + poss.slice(1)} core competencies include ${s1}, ${s2}, and ${s3} -- skills that ${subj} ${verbForm(d.perspective, 'have', 'has')} refined through years of hands-on experience.

${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}, a testament to ${poss} ability to identify opportunities and execute with precision. ${subj} ${v} deeply committed to ${pickRandom(passionVerbs)} building systems and processes that enable teams to do their best work and organizations to reach their full potential.

${poss.charAt(0).toUpperCase() + poss.slice(1)} approach is rooted in data-driven decision-making and a collaborative leadership style. ${subj} ${verbForm(d.perspective, 'believe', 'believes')} that great results come from empowering people, aligning strategy with execution, and maintaining an unwavering focus on the end goal.

${personalSentence} ${subj} ${verbForm(d.perspective, 'welcome', 'welcomes')} conversations about ${industry} trends, ${s1}, and new opportunities for collaboration.`,

      `${subj} ${v} an accomplished ${d.currentTitle}${company} who brings ${years} of ${industry} expertise to every engagement. With deep knowledge of ${s1}, ${s2}, and ${s3}, ${subj} ${verbForm(d.perspective, 'have', 'has')} helped organizations navigate change and achieve lasting results.

Among ${poss} most significant achievements, ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. This success was driven by a disciplined approach to strategy, a talent for building strong cross-functional relationships, and a relentless focus on quality.

${subj} ${v} recognized for ${pickRandom(leadershipPhrases)} and a consultative approach that puts stakeholder needs at the center of every decision. ${subj} ${verbForm(d.perspective, 'thrive', 'thrives')} in environments that demand both strategic thinking and tactical execution.

${personalSentence} ${subj} ${v} committed to professional growth and ${v} always eager to connect with like-minded professionals in the ${industry} community.`,

      `${pickRandom(connectorPhrases)} ${s1}, ${s2}, and ${s3}, ${subj} ${verbForm(d.perspective, 'serve', 'serves')} as a ${d.currentTitle}${company} with ${years} of proven success in ${industry}. ${poss.charAt(0).toUpperCase() + poss.slice(1)} work is defined by a commitment to ${pickRandom(impactPhrases)} and building sustainable solutions that stand the test of time.

A cornerstone of ${poss} career has been ${achievement}. This experience solidified ${poss} belief that ${s1}, when paired with strong ${s2} and ${s3}, can transform organizations from the inside out.

${subj} ${v} a firm believer in collaborative leadership, mentoring the next generation of professionals, and maintaining a forward-looking perspective on ${industry} developments. ${subj} ${verbForm(d.perspective, 'have', 'has')} ${pickRandom(leadershipPhrases)} and a reputation for exceeding expectations.

${personalSentence} Whether tackling a complex business challenge or mentoring a colleague, ${subj} ${verbForm(d.perspective, 'bring', 'brings')} the same level of energy and commitment.`,

      `${subj} ${v} a seasoned ${d.currentTitle}${company} with a career spanning ${years} in the ${industry} industry. ${poss.charAt(0).toUpperCase() + poss.slice(1)} expertise in ${s1}, ${s2}, and ${s3} has made ${objectForm(d.perspective)} a trusted partner for organizations seeking to ${pickRandom(closingOneLinerPhrases)}.

One of ${poss} proudest accomplishments is ${achievement}. This milestone exemplifies ${poss} approach: combining strategic insight with disciplined execution to generate outcomes that matter.

${subj} ${v} ${pickRandom(passionVerbs)} the evolving landscape of ${industry} and ${verbForm(d.perspective, 'stay', 'stays')} ahead of the curve by continuously investing in professional development and thought leadership. ${poss.charAt(0).toUpperCase() + poss.slice(1)} peers describe ${objectForm(d.perspective)} as someone who brings clarity to complexity and action to ambiguity.

${personalSentence} ${subj} ${v} always open to meaningful conversations and new opportunities to make an impact.`,

      `As a ${d.currentTitle}${company} with ${years} of experience, ${subj} ${verbForm(d.perspective, 'have', 'has')} developed deep proficiency in ${s1}, ${s2}, and ${s3} within the ${industry} sector. ${poss.charAt(0).toUpperCase() + poss.slice(1)} career has been defined by a relentless pursuit of ${pickRandom(impactPhrases)} and a passion for solving complex problems.

${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}, demonstrating ${poss} ability to operate at the intersection of strategy and execution. This experience has reinforced ${poss} conviction that the best outcomes are achieved through collaboration, data-informed decisions, and a willingness to challenge conventional thinking.

${subj} ${v} known for ${pickRandom(leadershipPhrases)} and an ability to inspire teams to reach new heights. ${subj} ${verbForm(d.perspective, 'take', 'takes')} pride in mentoring emerging talent and contributing to the broader ${industry} community.

${personalSentence} ${subj} ${verbForm(d.perspective, 'look', 'looks')} forward to connecting with professionals who share a passion for ${industry} and ${pickRandom(closingOneLinerPhrases)}.`,
    ],
    Friendly: [
      `${d.perspective === 'First' ? 'Hey there! I\'m' : `${d.fullName} is`} a ${d.currentTitle}${company} with ${years} in ${industry}. ${subj} ${verbForm(d.perspective, 'specialize', 'specializes')} in ${s1}, ${s2}, and ${s3} -- and ${subj} genuinely ${verbForm(d.perspective, 'love', 'loves')} what ${subj} ${verbForm(d.perspective, 'do', 'does')}.

What keeps ${objectForm(d.perspective)} going? The thrill of ${pickRandom(closingOneLinerPhrases)}. One of ${poss} favorite career moments was when ${subj} ${verbForm(d.perspective, 'was', 'was')} able to ${achievement}. It was the kind of challenge that reminded ${objectForm(d.perspective)} why ${subj} got into this field in the first place.

${subj} ${verbForm(d.perspective, 'believe', 'believes')} great work doesn't have to be stuffy or complicated. When ${s1} meets ${s2} with a healthy dose of ${s3}, amazing things happen. ${subj} ${verbForm(d.perspective, 'try', 'tries')} to bring that energy to every project and team ${subj} ${verbForm(d.perspective, 'work', 'works')} with.

${personalTouch ? `When ${subj} ${v} not working, you'll probably find ${objectForm(d.perspective)} ${personalTouch}. It keeps ${objectForm(d.perspective)} balanced and creative!` : `${subj} ${v} always up for a good conversation about ${industry}, new ideas, or what's next.`} Feel free to reach out -- ${subj} ${v} always happy to connect.`,

      `${subj} ${v} a ${d.currentTitle}${company} who has spent ${years} making ${industry} more interesting, one project at a time. ${poss.charAt(0).toUpperCase() + poss.slice(1)} go-to skills? ${s1}, ${s2}, and ${s3}.

${subj} ${v} especially proud of ${achievement}. It was challenging, rewarding, and proof that the right combination of skills and persistence can move mountains.

What makes ${objectForm(d.perspective)} tick is the chance to work with smart people on hard problems. ${subj} ${verbForm(d.perspective, 'bring', 'brings')} ${pickRandom(leadershipPhrases).toLowerCase()} and a collaborative spirit to every role. ${subj} ${v} the kind of person who ${verbForm(d.perspective, 'get', 'gets')} excited about a well-designed process almost as much as a well-designed product.

${personalTouch ? `Outside of work, ${subj} ${v} usually ${personalTouch}. It's ${poss} way of recharging.` : `${subj} ${v} also a firm believer in work-life balance and continuous learning.`} If you're in ${industry} or just want to chat about ${s1}, don't be a stranger!`,

      `With ${years} in ${industry}, ${subj} ${verbForm(d.perspective, 'have', 'has')} learned that the best results come from combining ${s1}, ${s2}, and ${s3} with genuine curiosity. As a ${d.currentTitle}${company}, ${subj} ${verbForm(d.perspective, 'get', 'gets')} to do exactly that every day.

${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}, which was a career highlight and a reminder of why ${subj} ${verbForm(d.perspective, 'love', 'loves')} this work. There's nothing quite like seeing a plan come together and making a real difference.

${subj} ${v} a big believer in building teams where everyone feels heard and empowered. ${subj} ${verbForm(d.perspective, 'think', 'thinks')} the best ideas come from diverse perspectives and a willingness to experiment.

${personalTouch ? `On ${poss} days off, you'll find ${objectForm(d.perspective)} ${personalTouch}.` : `${subj} ${v} always looking for the next interesting conversation or project.`} Let's connect!`,

      `${d.perspective === 'First' ? 'I\'m' : `${d.fullName} is`} a ${d.currentTitle}${company} and ${subj} ${verbForm(d.perspective, 'have', 'has')} spent the last ${years} immersed in the ${industry} world. ${poss.charAt(0).toUpperCase() + poss.slice(1)} toolkit includes ${s1}, ${s2}, and ${s3} -- and ${subj} ${verbForm(d.perspective, 'use', 'uses')} them to solve problems that matter.

Career highlight? Definitely ${achievement}. It took teamwork, creativity, and a lot of coffee, but the results were worth it.

${subj} ${v} the type who ${verbForm(d.perspective, 'ask', 'asks')} "what if?" a lot and ${verbForm(d.perspective, 'believe', 'believes')} that innovation doesn't have to mean reinventing the wheel. Sometimes the best solutions are the simplest.

${personalTouch ? `${pickRandom(outsideWorkPhrases)}, ${subj} ${verbForm(d.perspective, 'enjoy', 'enjoys')} ${personalTouch}. It keeps the creative juices flowing!` : `${subj} ${v} also passionate about mentoring and paying it forward.`} Always happy to chat about ${industry}, career development, or just swap good book recommendations.`,

      `${subj} ${v} a ${d.currentTitle}${company} who fell in love with ${industry} ${years} ago and hasn't looked back. Along the way, ${subj} ${verbForm(d.perspective, 'have', 'has')} developed deep skills in ${s1}, ${s2}, and ${s3}.

One achievement ${subj} ${v} particularly proud of: ${achievement}. This experience shaped ${poss} approach to work and reinforced ${poss} belief that people and process matter just as much as strategy.

${subj} ${v} known for bringing a positive, collaborative energy to every team ${subj} ${verbForm(d.perspective, 'join', 'joins')}. ${subj} ${verbForm(d.perspective, 'believe', 'believes')} that a great culture and clear communication are the foundation of any successful project.

${personalTouch ? `When not working, ${subj} ${verbForm(d.perspective, 'can', 'can')} be found ${personalTouch}.` : `${subj} ${v} always learning, always connecting.`} Drop ${objectForm(d.perspective)} a line if you want to talk ${industry} or ${s1}!`,

      `${years} in ${industry} have given ${objectForm(d.perspective)} a front-row seat to how ${s1}, ${s2}, and ${s3} can transform organizations. As a ${d.currentTitle}${company}, ${subj} ${verbForm(d.perspective, 'get', 'gets')} to put those insights into action every single day.

${subj} ${v} most proud of ${achievement}. It was one of those moments where everything clicked -- strategy, teamwork, and a little bit of luck.

What sets ${objectForm(d.perspective)} apart? ${subj} ${verbForm(d.perspective, 'genuinely care', 'genuinely cares')} about the people ${subj} ${verbForm(d.perspective, 'work', 'works')} with and the impact ${subj} ${verbForm(d.perspective, 'make', 'makes')}. ${subj} ${verbForm(d.perspective, 'approach', 'approaches')} everything with curiosity, empathy, and a healthy sense of humor.

${personalTouch ? `${pickRandom(outsideWorkPhrases)}, you'll find ${objectForm(d.perspective)} ${personalTouch}.` : `${subj} ${v} also big on giving back and mentoring others in the ${industry} space.`} Let's connect and swap stories.`,
    ],
    Bold: [
      `${subj} ${v} not your average ${d.currentTitle}. With ${years} in ${industry}, ${subj} ${verbForm(d.perspective, 'have', 'has')} built a reputation for ${pickRandom(impactPhrases)} -- not by following trends, but by setting them.

${poss.charAt(0).toUpperCase() + poss.slice(1)} expertise in ${s1}, ${s2}, and ${s3} isn't theoretical. ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}, and ${subj} ${verbForm(d.perspective, 'did', 'did')} it by challenging assumptions and pushing past every boundary.

${subj} ${verbForm(d.perspective, 'believe', 'believes')} that ${industry} needs fewer people who maintain the status quo and more people who break it. Every project ${subj} ${verbForm(d.perspective, 'touch', 'touches')} is an opportunity to raise the bar, and ${subj} ${verbForm(d.perspective, 'bring', 'brings')} ${pickRandom(leadershipPhrases).toLowerCase()} to make that happen.

${personalTouch ? `When not shaking up the ${industry} world, ${subj} ${v} usually ${personalTouch}.` : `${subj} ${v} always pushing limits -- in work and in life.`} If you want safe, predictable results, look elsewhere. If you want extraordinary -- let's talk.`,

      `${years}. ${d.currentTitle}${company}. ${industry}. ${subj} ${verbForm(d.perspective, 'have', 'has')} spent every one of those years proving that ${s1}, ${s2}, and ${s3} aren't just buzzwords -- they're the keys to unlocking real results.

The proof? ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. And ${subj} ${v} just getting warmed up.

${subj} ${verbForm(d.perspective, 'don\'t', 'doesn\'t')} believe in half measures. When ${subj} ${verbForm(d.perspective, 'commit', 'commits')} to a project, ${subj} ${verbForm(d.perspective, 'go', 'goes')} all in -- bringing the kind of intensity and focus that turns good ideas into game-changing outcomes. ${subj} ${verbForm(d.perspective, 'lead', 'leads')} teams that punch above their weight and deliver results that speak for themselves.

${personalTouch ? `Off the clock, ${subj} ${verbForm(d.perspective, 'channel', 'channels')} that same energy into ${personalTouch}.` : `That same drive extends to everything ${subj} ${verbForm(d.perspective, 'do', 'does')}.`} Ready to make something happen? ${d.perspective === 'First' ? 'I\'m listening.' : 'Reach out.'}`,

      `In a world full of ${d.currentTitle}s who play it safe, ${subj} ${verbForm(d.perspective, 'choose', 'chooses')} to play it bold. ${subj} ${verbForm(d.perspective, 'have', 'has')} spent ${years} in ${industry} mastering ${s1}, ${s2}, and ${s3} -- and using them to ${pickRandom(closingOneLinerPhrases)}.

The numbers tell the story: ${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. That's what happens when ambition meets expertise meets relentless execution.

${subj} ${v} the person teams call when the stakes are high and the margin for error is low. ${poss.charAt(0).toUpperCase() + poss.slice(1)} approach is simple: define the goal, build the strategy, execute without excuses. ${subj} ${verbForm(d.perspective, 'have', 'has')} ${pickRandom(leadershipPhrases).toLowerCase()} and zero tolerance for mediocrity.

${personalTouch ? `${pickRandom(outsideWorkPhrases)}, ${subj} ${v} probably ${personalTouch} -- because intensity is a lifestyle, not just a work ethic.` : `${subj} ${v} always looking for the next big challenge.`} The question is: are you ready?`,

      `${subj} ${verbForm(d.perspective, 'didn\'t', 'didn\'t')} become a ${d.currentTitle}${company} by accident. ${years} of ${industry} experience, expertise in ${s1}, ${s2}, and ${s3}, and an appetite for challenges that make other people nervous -- that's what got ${objectForm(d.perspective)} here.

${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement}. Not because it was easy, but because ${subj} ${verbForm(d.perspective, 'refuse', 'refuses')} to settle for anything less than exceptional. Every obstacle is just a problem waiting for a better solution.

${poss.charAt(0).toUpperCase() + poss.slice(1)} leadership style? Direct, decisive, and deeply invested in the people ${subj} ${verbForm(d.perspective, 'work', 'works')} with. ${subj} ${verbForm(d.perspective, 'build', 'builds')} teams that are empowered, aligned, and unafraid to take big swings.

${personalTouch ? `Beyond the boardroom, ${subj} ${v} just as driven -- ${d.personalTouch} keeps ${objectForm(d.perspective)} sharp.` : `${subj} ${v} always hunting for the next frontier.`} Bring ${objectForm(d.perspective)} your hardest problem. Let's solve it.`,

      `${years} in ${industry}. One mission: ${pickRandom(closingOneLinerPhrases)}. ${subj} ${v} a ${d.currentTitle}${company} who lives for the moments when ${s1}, ${s2}, and ${s3} converge to create something extraordinary.

${subj} ${verbForm(d.perspective, 'have', 'has')} ${achievement} -- not by following the playbook, but by rewriting it. ${subj} ${verbForm(d.perspective, 'see', 'sees')} opportunities where others see obstacles, and ${subj} ${verbForm(d.perspective, 'execute', 'executes')} where others hesitate.

What sets ${objectForm(d.perspective)} apart isn't just experience -- it's intensity. ${subj} ${verbForm(d.perspective, 'bring', 'brings')} ${pickRandom(leadershipPhrases).toLowerCase()} combined with a relentless drive to exceed every benchmark and expectation placed in ${poss} path.

${personalTouch ? `Even outside of ${industry}, ${subj} ${verbForm(d.perspective, 'bring', 'brings')} the same fire -- ${personalTouch} is just another arena for excellence.` : `${subj} ${v} not done yet. Not even close.`} If you're looking for a partner who will outwork, outthink, and outdeliver -- ${d.perspective === 'First' ? 'I\'m right here.' : `${d.fullName} is the one to call.`}`,

      `Conventional wisdom says ${d.currentTitle}s should stay in their lane. ${subj} ${verbForm(d.perspective, 'say', 'says')} lanes are overrated. With ${years} in ${industry}, ${subj} ${verbForm(d.perspective, 'have', 'has')} mastered ${s1}, ${s2}, and ${s3} -- and ${subj} ${verbForm(d.perspective, 'use', 'uses')} them all to ${pickRandom(impactPhrases)}.

The highlight reel includes ${achievement}. But ${subj} ${v} more interested in what's next than what's behind ${objectForm(d.perspective)}.

${subj} ${verbForm(d.perspective, 'lead', 'leads')} with conviction, ${verbForm(d.perspective, 'communicate', 'communicates')} with clarity, and ${verbForm(d.perspective, 'deliver', 'delivers')} with consistency. ${poss.charAt(0).toUpperCase() + poss.slice(1)} teams know that ${subj} ${verbForm(d.perspective, 'expect', 'expects')} the best from them because ${subj} ${verbForm(d.perspective, 'demand', 'demands')} it from ${d.perspective === 'First' ? 'myself' : `${d.fullName}`} first.

${personalTouch ? `When ${subj} ${v} not disrupting ${industry}, ${subj} ${v} probably ${personalTouch}.` : `${subj} ${v} always in motion, always building.`} Let's create something worth talking about.`,
    ],
  };

  return pickRandom(templates[d.tone]);
}

/* ------------------------------------------------------------------ */
/*  Main Component                                                     */
/* ------------------------------------------------------------------ */

export default function BioGeneratorContent() {
  const [formData, setFormData] = useState<FormData>({
    fullName: '',
    currentTitle: '',
    company: '',
    industry: '',
    yearsExperience: '',
    skills: '',
    achievement: '',
    personalTouch: '',
    perspective: 'First',
    tone: 'Professional',
  });

  const [bios, setBios] = useState<GeneratedBio[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const generateAllBios = () => {
    const oneLiner = generateOneLiner(formData);
    const shortParagraph = generateShortParagraph(formData);
    const fullBio = generateFullBio(formData);

    setBios([
      {
        label: 'One-Liner',
        description: 'Perfect for social profiles, email signatures, and quick intros',
        text: oneLiner,
        targetWords: '~25 words',
        icon: <User className="w-5 h-5" />,
      },
      {
        label: 'Short Paragraph',
        description: 'Great for speaker intros, team pages, and conference programs',
        text: shortParagraph,
        targetWords: '~75 words',
        icon: <MessageSquare className="w-5 h-5" />,
      },
      {
        label: 'Full Bio',
        description: 'Ideal for websites, portfolios, and detailed profiles',
        text: fullBio,
        targetWords: '~150-200 words',
        icon: <FileText className="w-5 h-5" />,
      },
    ]);
    setHasGenerated(true);
  };

  const regenerateSingle = (index: number) => {
    const newBios = [...bios];
    if (index === 0) {
      newBios[0] = { ...newBios[0], text: generateOneLiner(formData) };
    } else if (index === 1) {
      newBios[1] = { ...newBios[1], text: generateShortParagraph(formData) };
    } else {
      newBios[2] = { ...newBios[2], text: generateFullBio(formData) };
    }
    setBios(newBios);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName.trim() || !formData.currentTitle.trim()) return;
    generateAllBios();
  };

  const inputClass =
    'w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-shadow text-gray-900';
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-1.5';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-stone-100">
      <ToolHero
        title="Professional Bio Generator"
        description="Create professional bios in 3 lengths: one-liner, short paragraph, and full bio. Perfect for websites, LinkedIn, speaker profiles, and portfolios."
        category="Career Tools"
      />

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Input Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Tell us about yourself
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {/* Full Name */}
            <div>
              <label htmlFor="fullName" className={labelClass}>
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                required
                value={formData.fullName}
                onChange={(e) => updateField('fullName', e.target.value)}
                placeholder="e.g., Jane Smith"
                className={inputClass}
              />
            </div>

            {/* Current Title */}
            <div>
              <label htmlFor="currentTitle" className={labelClass}>
                Current Title <span className="text-red-500">*</span>
              </label>
              <input
                id="currentTitle"
                type="text"
                required
                value={formData.currentTitle}
                onChange={(e) => updateField('currentTitle', e.target.value)}
                placeholder="e.g., Product Manager, Software Engineer"
                className={inputClass}
              />
            </div>

            {/* Company */}
            <div>
              <label htmlFor="company" className={labelClass}>
                Company
              </label>
              <input
                id="company"
                type="text"
                value={formData.company}
                onChange={(e) => updateField('company', e.target.value)}
                placeholder="e.g., Google, Acme Corp"
                className={inputClass}
              />
            </div>

            {/* Industry */}
            <div>
              <label htmlFor="industry" className={labelClass}>
                Industry
              </label>
              <input
                id="industry"
                type="text"
                value={formData.industry}
                onChange={(e) => updateField('industry', e.target.value)}
                placeholder="e.g., FinTech, Healthcare, SaaS"
                className={inputClass}
              />
            </div>

            {/* Years of Experience */}
            <div>
              <label htmlFor="yearsExperience" className={labelClass}>
                Years of Experience
              </label>
              <input
                id="yearsExperience"
                type="text"
                value={formData.yearsExperience}
                onChange={(e) => updateField('yearsExperience', e.target.value)}
                placeholder="e.g., 8, 15"
                className={inputClass}
              />
            </div>

            {/* Perspective */}
            <div>
              <label htmlFor="perspective" className={labelClass}>
                Perspective
              </label>
              <select
                id="perspective"
                value={formData.perspective}
                onChange={(e) => updateField('perspective', e.target.value)}
                className={inputClass}
              >
                <option value="First">First Person (I am...)</option>
                <option value="Third">Third Person (Jane is...)</option>
              </select>
            </div>

            {/* Top 3 Skills */}
            <div className="sm:col-span-2">
              <label htmlFor="skills" className={labelClass}>
                Top 3 Skills or Areas of Expertise
              </label>
              <input
                id="skills"
                type="text"
                value={formData.skills}
                onChange={(e) => updateField('skills', e.target.value)}
                placeholder="e.g., data analysis, team leadership, strategic planning (comma-separated)"
                className={inputClass}
              />
            </div>

            {/* Biggest Achievement */}
            <div className="sm:col-span-2">
              <label htmlFor="achievement" className={labelClass}>
                Biggest Achievement
              </label>
              <textarea
                id="achievement"
                value={formData.achievement}
                onChange={(e) => updateField('achievement', e.target.value)}
                placeholder="e.g., led a product launch that generated $2M in first-year revenue"
                rows={2}
                className={`${inputClass} resize-none`}
              />
            </div>

            {/* Personal Touch */}
            <div className="sm:col-span-2">
              <label htmlFor="personalTouch" className={labelClass}>
                Personal Touch (hobby or interest)
              </label>
              <input
                id="personalTouch"
                type="text"
                value={formData.personalTouch}
                onChange={(e) => updateField('personalTouch', e.target.value)}
                placeholder="e.g., hiking in the Rockies, volunteering at local schools, playing jazz piano"
                className={inputClass}
              />
            </div>

            {/* Tone */}
            <div className="sm:col-span-2">
              <label className={labelClass}>Tone</label>
              <div className="flex flex-wrap gap-3 mt-1">
                {(['Professional', 'Friendly', 'Bold'] as Tone[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => updateField('tone', t)}
                    className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-colors ${
                      formData.tone === t
                        ? 'bg-blue-600 text-white shadow'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {t === 'Professional' && 'Professional'}
                    {t === 'Friendly' && 'Friendly'}
                    {t === 'Bold' && 'Bold'}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Generate Button */}
          <button
            type="submit"
            className="mt-8 w-full sm:w-auto bg-gradient-to-r from-blue-600 to-blue-500 text-white font-bold py-3 px-10 rounded-xl shadow-lg hover:from-blue-700 hover:to-blue-600 transition-all text-lg"
          >
            Generate Bios
          </button>
        </form>

        {/* Results */}
        {hasGenerated && bios.length > 0 && (
          <section className="mt-12 space-y-6">
            <h2 className="text-2xl font-extrabold text-gray-900">
              Your Professional Bios
            </h2>

            {bios.map((bio, idx) => {
              const wordCount = countWords(bio.text);
              return (
                <div
                  key={idx}
                  className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 sm:p-8"
                >
                  {/* Header */}
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-blue-100 text-blue-600">
                        {bio.icon}
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">
                          {bio.label}
                        </h3>
                        <p className="text-sm text-gray-500">
                          {bio.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700">
                        {wordCount} words (target: {bio.targetWords})
                      </span>
                    </div>
                  </div>

                  {/* Bio Text */}
                  <div className="bg-gray-50 rounded-xl p-5 mb-4">
                    <p className="text-gray-800 leading-relaxed whitespace-pre-line">
                      {bio.text}
                    </p>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <CopyButton text={bio.text} label="Copy" />
                    <button
                      onClick={() => regenerateSingle(idx)}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Regenerate
                    </button>
                  </div>
                </div>
              );
            })}
          </section>
        )}

        {/* Tips Section */}
        <section className="mt-16">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-100 text-amber-600">
                <Lightbulb className="w-5 h-5" />
              </div>
              <h2 className="text-2xl font-extrabold text-gray-900">
                Tips for Writing a Great Professional Bio
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Lead with your value',
                  description:
                    'Start with what you do and the impact you create, not just your job title. Readers should immediately understand the value you bring.',
                },
                {
                  title: 'Quantify your achievements',
                  description:
                    'Numbers and specifics make your bio more credible. Instead of "improved sales," say "increased revenue by 40% in 12 months."',
                },
                {
                  title: 'Match the tone to the platform',
                  description:
                    'Use a professional tone for corporate sites, a friendly tone for personal blogs, and a bold tone for speaker bios and portfolios.',
                },
                {
                  title: 'Include a personal touch',
                  description:
                    'A brief mention of a hobby or interest makes you relatable and memorable. It shows you are a real person, not just a resume.',
                },
                {
                  title: 'Keep multiple versions ready',
                  description:
                    'Different platforms need different lengths. Have a one-liner, short paragraph, and full bio prepared so you are always ready.',
                },
                {
                  title: 'Update regularly',
                  description:
                    'Your bio should evolve with your career. Review and refresh it every 6 months or whenever you hit a major milestone.',
                },
              ].map((tip, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{tip.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">
                      {tip.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-500">
              <strong>Sources &amp; Further Reading:</strong>{' '}
              <a href="https://hbr.org/topic/career-planning" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Harvard Business Review: Career Planning</a>
              {' · '}
              <a href="https://www.indeed.com/career-advice/resumes-cover-letters/how-to-write-a-professional-bio" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Indeed: How to Write a Professional Bio</a>
              {' · '}
              <a href="https://www.bls.gov/ooh/" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Bureau of Labor Statistics: Occupational Outlook</a>
            </p>
          </div>
        </section>

        {/* CTA */}
        <ToolCTA
          headline="Your bio introduces you. Your resume gets you hired."
          description="Create a tailored, ATS-optimized resume in minutes with Superpower Resume. Stand out to recruiters and land more interviews."
          buttonText="Build My Resume Free"
        />
      </div>
    </div>
  );
}
